import { NONE_OPTION } from 'components/Sell/utils/common'
import { groupBy, keys } from 'lodash-es'
import {
    ModifierOptionContract,
    OrderLineModifierSlimModel,
    OrderLineLookupContract,
} from 'models'
import {
    EntityModifierViewModel,
    ModifierOptionData,
    ModifierOptionLookup,
    PriceListData,
    UsedModifiers,
    UsedSubModifier,
} from 'store/models/sellModels'
import { updatePriceListModifierGroupData } from 'utilities/price/getPrice'

export function buildUsedModifiers(
    line: OrderLineLookupContract,
    modifierOptionsMap: { [key: string]: ModifierOptionContract },
    modifierList: EntityModifierViewModel[],
    priceListData: PriceListData,
) {
    const usedModifiers: UsedModifiers = {}
    const oldModifierList: EntityModifierViewModel[] = []
    const oldModifierOptionMaps: ModifierOptionLookup = {}

    if (line.orderLineModifiers) {
        const bindingOptionGroups = groupBy(
            line.orderLineModifiers,
            (e) => e.bindingModifierGroupID,
        )

        const itemModifiers = keys(bindingOptionGroups).filter((bId) =>
            bindingOptionGroups[bId]?.some((a) => !a.parentOrderLineModifierID),
        )

        itemModifiers.forEach((itemModifierOptionId) => {
            const existModEnList = modifierList.find(
                (a) => a.entityModifierId === itemModifierOptionId,
            )

            const isItemHasCostMod = existModEnList?.hasAssociatedCost ?? true

            checkBindingEntity(
                existModEnList,
                bindingOptionGroups[itemModifierOptionId],
                itemModifierOptionId,
                oldModifierList,
                line.itemID!,
            )
            usedModifiers[itemModifierOptionId] = []

            bindingOptionGroups[itemModifierOptionId].forEach(
                (modifierLine) => {
                    const quantity = modifierLine.quantity ?? 1
                    let bindingOptionModifierMaps = groupBy(
                        line.orderLineModifiers?.filter(
                            (o) =>
                                o.parentOrderLineModifierID ===
                                modifierLine.orderLineModifierID,
                        ),
                        (a) => a.bindingModifierGroupID,
                    )
                    let subChilds: UsedSubModifier = {}
                    keys(bindingOptionModifierMaps).forEach((BOptionId) => {
                        subChilds[BOptionId] = bindingOptionModifierMaps[
                            BOptionId
                        ].flatMap<ModifierOptionData>(
                            (c: OrderLineModifierSlimModel) => {
                                const options: ModifierOptionData[] = []
                                for (let i = 0; i < (c.quantity ?? 0); i++) {
                                    options.push({
                                        modifierOptionId:
                                            c.modifierOptionID ?? NONE_OPTION,
                                        addPrice: c.priceAdd ?? 0,
                                    })
                                }
                                return options
                            },
                        )

                        const existSubModEnList = modifierList.find(
                            (a) => a.entityModifierId === BOptionId,
                        )
                        const maxSelectable =
                            existSubModEnList?.maxSelectedOptions ?? 0
                        const noneSelectCount =
                            maxSelectable - subChilds[BOptionId].length

                        const hasCost =
                            existSubModEnList?.hasAssociatedCost ?? true
                        for (let i = 0; i < noneSelectCount; i++) {
                            subChilds[BOptionId].push({
                                modifierOptionId: NONE_OPTION,
                                addPrice: 0,
                            })
                        }
                        checkBindingEntity(
                            existSubModEnList,
                            bindingOptionModifierMaps[BOptionId],
                            BOptionId,
                            oldModifierList,
                            modifierLine.modifierOptionID!,
                        )
                        bindingOptionModifierMaps[BOptionId].forEach(
                            (option) => {
                                checkModifierOption(
                                    modifierOptionsMap,
                                    option,
                                    line.menuID!,
                                    oldModifierOptionMaps,
                                    hasCost,
                                    priceListData,
                                )
                            },
                        )
                    })
                    checkModifierOption(
                        modifierOptionsMap,
                        modifierLine,
                        line.menuID!,
                        oldModifierOptionMaps,
                        isItemHasCostMod,
                        priceListData,
                    )
                    for (let i = 0; i < quantity; i++)
                        usedModifiers[itemModifierOptionId].push({
                            modifierOptionId: modifierLine.modifierOptionID!,
                            addPrice: modifierLine.priceAdd ?? 0,
                            subModifierOptions: subChilds,
                        })
                },
            )
        })
    }
    return { usedModifiers, oldModifierList, oldModifierOptionMaps }
}

function checkModifierOption(
    modifierOptionsMap: { [key: string]: ModifierOptionContract },
    option: OrderLineModifierSlimModel | undefined,
    menuID: string,
    oldModifierOptionMaps: ModifierOptionLookup,
    hasAssociatedCost: boolean,
    priceListData: PriceListData,
) {
    const existOption = modifierOptionsMap?.[option?.modifierOptionID!]

    if (!existOption) {
        updatePriceListModifierGroupData(
            priceListData,
            option?.modifierOption,
            option?.priceAdd ?? 0,
        )
    }
    if (!(existOption || oldModifierOptionMaps[option?.modifierOptionID!])) {
        oldModifierOptionMaps[
            option?.modifierOptionID!
        ] = option?.modifierOption!
    }
}

function checkBindingEntity(
    existModEnList: EntityModifierViewModel | undefined,
    options: OrderLineModifierSlimModel[],
    bId: string,
    oldModifierList: EntityModifierViewModel[],
    entityId: string,
) {
    if (
        !existModEnList &&
        !oldModifierList.some((a) => a.entityModifierId === bId)
    ) {
        //restore sub child
        oldModifierList.push(
            MockBindingEntity(
                bId,
                options?.[0]?.modifierOption?.modifierGroupID!,
                entityId,
                true,
                Number.MAX_SAFE_INTEGER,
            ),
        )
    }
}

function MockBindingEntity(
    bindingId: string,
    modGroupId: string,
    entityId: string,
    hasAssociatedCost: boolean,
    sortNumber: number,
) {
    return {
        entityModifierId: bindingId,
        entityId: entityId,
        isActive: false,
        hasAssociatedCost: hasAssociatedCost,
        displayName: '#itemModifiers',
        maxSelectedOptions: 0,
        minSelectedOptions: 0,
        modifierGroupId: modGroupId,
        isRequired: false,
        sortNumber: sortNumber,
    }
}
